<template>
  <div class="fts-comment">
    <!-- Uploaded Attachments -->
    <fts-file
      class="comment-file-attachments"
      :prefixURL="`${vectorId}-comment/`" 
      :data="comment.attachments" 
      editable 
      @removeFile="removeFile($event)" />
    <!-- Comment -->
    <div class="comment-wrapper" @paste="onPaste">
      <!-- Comment Content -->
      <div class="comment-content">
        <!-- Queue Attachments -->
        <fts-file 
          class="comment-uploader" 
          :data="files" 
          cancellable
          @cancelUpload="cancelUpload" />
        <!-- Editor -->
        <div class="comment-editor fts-scrollbar" :style="{ maxHeight: editorMaxHeight }">      
          <editor-content class="editor__content fts-editor__content" :editor="editor" />
        </div>
        <!-- Actions -->
        <div class="comment-action-bar">
          <div class="comment-actions">
            <!-- File -->
            <div class="file-upload-container icon-handle">
              <file-upload
                ref="upload"
                v-model="files"
                :drop="true"
                :multiple="true"
                :post-action="fileUploadURL"
                :data="{ vectorId: vectorId, fieldName: 'files' }"
                :input-id="`paperclip${id}`"
                @input-file="inputFile"
                @input-filter="inputFilter"
              >
                <font-awesome-icon
                  class="comment-action-icon"
                  :icon="['fas', 'paperclip']"
                  fixed-width
                />
              </file-upload>
            </div>
            <!-- Emoji -->
            <div>
              <fts-emoji 
                appendToBody 
                :toggleSize="20" 
                @updateEmoji="emojiOnUpdated" />
            </div>
            <!-- Tag -->
            <div @click="insertTag">
              <font-awesome-icon
                class="comment-action-icon"
                :icon="['fas', 'at']"
                fixed-width
              />
            </div>
          </div>
          <!-- Comment Send -->
          <fts-btn 
            icon 
            text 
            sm 
            color="primary" 
            :loading="submitting"
            :disabled="!enabledSend"
            @click.native.prevent="send"
            v-if="sendAction">
            <font-awesome-icon :icon="['far', 'paper-plane']" fixed-width />
          </fts-btn>
        </div>
      </div>
    </div>
    <!-- Users -->
    <div class="suggestion-list" v-show="showSuggestions" ref="suggestions">
      <template v-if="hasResults">
        <div
          v-for="(user, index) in filteredUsers"
          :key="`${user.userName || user.username}-${index}`"
          class="suggestion-list__item"
          :class="{ 'is-selected': navigatedUserIndex === index }"
          @click.stop="selectUser(user)"
        >
          <fts-profile :profile="user" :profile-photo-size="30">
            <template #profileName>
              <div class="user-profile-name">{{ user.displayName }}</div>
              <div class="user-profile-username">{{ user.userName || user.username }}</div>
            </template>
          </fts-profile>
        </div>
      </template>
      <div v-else class="suggestion-list__item is-empty">No users found</div>
    </div>
  </div>
</template>

<script>
import { classificationStore } from "../store/classificationStore";
import webAppConfig from '../commons/webApp.config';
import commons from "../commons/commons";
import { htmlValidator } from "../commons/validator";

import Vue from "vue";
import tippy from "../tippy-4.3.5";
import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
  HardBreak,
  Mention,
  Bold,
  Italic,
  Placeholder,
  History,
} from "tiptap-extensions";
import VueUploadComponent from "../vue-upload-component/index";
import ImageCompressor from "compressorjs";
import Doc from "../library/doc";

import FTSEmoji from "./FTSEmoji";
import FTSProfile from "./FTSProfile";
import FTSFile from "./file/FTSFile";
import FTSBtn from "./FTSBtn";

Vue.component("file-upload", VueUploadComponent);

export default {
  props: {
    vectorId: [String, Number],
    locusId: [String, Number],
    locusDetailId: [String, Number],
    locusCollaborationId: [String, Number],
    comment: {
      type: Object,
      default: () => ({}),
    },
    avatar: {
      type: Boolean,
      default: true,
    },
    sendAction: Boolean,
    editorMaxHeight: {
      type: String,
      default: '120px'
    },
  },
  model: {
    prop: "comment",
    event: "updateComment"
  },
  components: {
    EditorContent,
    FileUpload: VueUploadComponent,
    "fts-emoji": FTSEmoji,
    "fts-profile": FTSProfile,
    "fts-file": FTSFile,
    "fts-btn": FTSBtn
  },
  data() {
    let vm = this;
    return {
      id: null,
      files: [],
      showTool: true,
      editor: new Editor({
        onUpdate: ({ getHTML }) => {
          const state = getHTML();
          vm.$set(vm.comment, 'content', state)
        },
        extensions: [
          new Doc(),
          new HardBreak(),
          new Placeholder({
            emptyEditorClass: 'is-editor-empty',
            emptyNodeClass: 'is-empty',
            showOnlyWhenEditable: true,
            showOnlyCurrent: true,
            emptyNodeText: (node) => {
              return "write something...";
            },
          }),
          new Mention({
            // a list of all suggested items
            items: () => this.users,
            // is called when a suggestion starts
            onEnter: ({ items, query, range, command, virtualNode }) => {
              this.query = query;
              this.filteredUsers = items;
              this.suggestionRange = range;
              this.renderPopup(virtualNode);
              // we save the command for inserting a selected mention
              // this allows us to call it inside of our custom popup
              // via keyboard navigation and on click
              this.insertMention = command;
            },
            // is called when a suggestion has changed
            onChange: ({ items, query, range, virtualNode }) => {
              this.query = query;
              this.filteredUsers = items;
              this.suggestionRange = range;
              this.navigatedUserIndex = 0;
              this.renderPopup(virtualNode);
            },
            // is called when a suggestion is cancelled
            onExit: () => {
              // reset all saved values
              this.query = null;
              this.filteredUsers = [];
              this.suggestionRange = null;
              this.navigatedUserIndex = 0;
              this.destroyPopup();
            },
            // is called on every keyDown event while a suggestion is active
            onKeyDown: ({ event }) => {
              // pressing up arrow
              if (event.keyCode === 38) {
                this.upHandler();
                return true;
              }
              // pressing down arrow
              if (event.keyCode === 40) {
                this.downHandler();
                return true;
              }
              // pressing enter
              if (event.keyCode === 13) {
                this.enterHandler();
                return true;
              }
              return false;
            },
            // is called when a suggestion has changed
            // this function is optional because there is basic filtering built-in
            // you can overwrite it if you prefer your own filtering
            // in this example we use fuse.js with support for fuzzy search
            onFilter: (items, query) => {
              if (query) {
                let hardbreak = query.indexOf('\u0000');
                if(hardbreak > -1) {
                  query = query.substring(hardbreak, query.length - hardbreak);
                }
                return items
                  .filter(
                    (x) =>
                      (x.displayName.toLowerCase().indexOf(query.toLowerCase()) >
                      -1
                      || x.username.toLowerCase().indexOf(query.toLowerCase()) >
                      -1) && !x.disable
                  )
                  .slice(0, 10);
              }
              return items.slice(0, 10);
            },
          }),
          new Bold(),
          new Italic(),
          new History(),
        ],
        content: this.comment.content,
      }),
      query: null,
      suggestionRange: null,
      filteredUsers: [],
      navigatedUserIndex: 0,
      insertMention: () => {},
      observer: null,
      submitting: false
    };
  },
  computed: {
    fileUploadURL() {
      return webAppConfig.fileUploadURL;
    },
    users() {
      return classificationStore.state.users.filter(x => !x.disable);
    },
    hasResults() {
      return this.filteredUsers.length;
    },
    showSuggestions() {
      return this.query || this.hasResults;
    },
    currentUsername() {
      return classificationStore.state.currentUsername;
    },
    enabledSend() {
      return this.comment && (this.comment.attachments && this.comment.attachments.length || htmlValidator(this.comment.content))
    },
    autoCompress() {
      return 500 * 1024;
    },
  },
  methods: {
    async send() {
      if (this.enabledSend) {
        this.submitting = true
        // Locus Id
        this.comment.locusId = this.locusId;
        // Locus Detail Id
        if (this.locusDetailId) 
          this.comment.locusDetailId = parseInt(this.locusDetailId);
        // Locus Collaboration Id
        if (this.locusCollaborationId) 
          this.comment.locusCollaborationId = parseInt(this.locusCollaborationId);
        // Send API
        try {
          let res = await classificationStore.dispatch("sendComment", this.comment);
          this.$emit('updateComment', {});
          this.editor.clearContent();
          this.$emit("added", res.data);
        } catch (err) {}
        
        this.submitting = false;
      }
      // escape
    },
    emojiOnUpdated(item) {
      const transaction = this.editor.state.tr.insertText(item);
      this.editor.view.dispatch(transaction);
      this.editor.focus();
    },
    // navigate to the previous item
    // if it's the first item, navigate to the last one
    upHandler() {
      this.navigatedUserIndex =
        (this.navigatedUserIndex + this.filteredUsers.length - 1) %
        this.filteredUsers.length;
    },
    // navigate to the next item
    // if it's the last item, navigate to the first one
    downHandler() {
      this.navigatedUserIndex =
        (this.navigatedUserIndex + 1) % this.filteredUsers.length;
    },
    enterHandler() {
      const user = this.filteredUsers[this.navigatedUserIndex];
      if (user) {
        this.selectUser(user);
      }
    },
    // we have to replace our suggestion text with a mention
    // so it's important to pass also the position of your suggestion text
    selectUser(user) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: user.userName || user.username,
          label: user.userName || user.username,
        },
      });
      this.editor.focus();
    },
    // renders a popup with suggestions
    // tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
    renderPopup(node) {
      if (this.popup) {
        return;
      }
      this.popup = tippy(node, {
        content: this.$refs.suggestions,
        trigger: "mouseenter",
        interactive: true,
        theme: "dark",
        placement: "top-start",
        inertia: true,
        duration: [400, 200],
        showOnInit: true,
        arrow: true,
        arrowType: "round",
      });
      // we have to update tippy whenever the DOM is updated
      if (MutationObserver) {
        this.observer = new MutationObserver(() => {
          this.popup.popperInstance.scheduleUpdate();
        });
        this.observer.observe(this.$refs.suggestions, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    },
    destroyPopup() {
      if (this.popup) {
        this.popup.destroy();
        this.popup = null;
      }
      if (this.observer) {
        this.observer.disconnect();
      }
    },
    //upload
    onPaste() {
      let vm = this;
      var items = (event.clipboardData || event.originalEvent.clipboardData).items;

      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (item.kind === "file") {
          //item.name = "test.png";
          //vm.$refs.addDataTransfer(file);
          var file = item.getAsFile();
          //file.name = 'test.png'
          vm.$refs.upload.add(file, { active: false });
        }
      }
    },
    /**
     * Has changed
     * @param  Object|undefined   newFile   只读
     * @param  Object|undefined   oldFile   只读
     * @return undefined
     */
    inputFile(newFile, oldFile) {
      if (newFile && oldFile) {
        /* before send */
        if (newFile.active && !oldFile.active) {
          if (newFile.name === "image.png" || newFile.name === "unknown")
            newFile.name = `${commons.nanoID()}.png`;
          else
            newFile.name = newFile.name.replaceAll("/", "_");
        }
        /* on progress */
        if (newFile.progress !== oldFile.progress) {
          // progress
        }
        /* on error */
        if (newFile.error && !oldFile.error) {
          if (newFile.xhr) {
            /* xhr status */
            console.log("status", newFile.xhr.status);
            /* xhr error handling */
            if (newFile.xhr.status === 413)
              this.$toasted.error('Upload failed. Request entity too large.');
            else if (newFile.xhr.status === 500)
              this.$toasted.error('Upload failed. Something went wrong.');
          } else if (newFile.error == "size") {
            /* size error handling */
            this.$toasted.error(`Upload failed. File size can not exceed ${this.item.maxFileSize} MB.`);
          }
        }
        /* on success */
        if (newFile.success && !oldFile.success) {
          let attachments = [];
          try {
            attachments = [...this.comment.attachments];
          } catch (err) { 
            //
          }
          attachments.push({
            path: newFile.response.url,
            name: newFile.response.filename,
            size: newFile.response.size
          });
          this.$set(this.comment, 'attachments', attachments);
        }
        /* remove file from queue */
        if (!newFile.active && oldFile.active) {
          this.removeQueueFile(newFile);
        }
      }
      /* remove */
      if (!newFile && oldFile) {
        if (oldFile.success && oldFile.response.id) {
          // delete
        }
      }
      /* automatically activate upload */
      if (
        Boolean(newFile) !== Boolean(oldFile) ||
        oldFile.error !== newFile.error
      ) {
        if (!this.$refs.upload.active) {
          this.$refs.upload.active = true;
        }
      }
    },
    /**
     * Pretreatment
     * @param  Object|undefined   newFile   读写
     * @param  Object|undefined   oldFile   只读
     * @param  Function           prevent   阻止回调
     * @return undefined
     */
    inputFilter(newFile, oldFile) {
      if (newFile && !oldFile) {
        // Automatic compression
        // 自动压缩
        if (
          newFile.file &&
          newFile.type.substr(0, 6) === "image/" &&
          this.autoCompress > 0 &&
          this.autoCompress < newFile.size
        ) {
          let vm = this;

          newFile.error = "compressing";
          const imageCompressor = new ImageCompressor(newFile.file, {
            maxWidth: 1200,
            maxHeight: 1200,
            success(file) {
              vm.$refs.upload.update(newFile, {
                error: "",
                file,
                size: file.size,
                type: file.type,
              });
            },
            error(err) {
              vm.$refs.upload.update(newFile, {
                error: err.message || "compress",
              });
            },
          });
        }
      }

      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        if (newFile.file != null) {
          // Create a blob field
          // 创建 blob 字段
          newFile.blob = "";
          let URL = window.URL || window.webkitURL;
          if (URL && URL.createObjectURL) {
            newFile.blob = URL.createObjectURL(newFile.file);
          }

          newFile.thumb = "";
          if (newFile.blob && newFile.type.substr(0, 6) === "image/") {
            newFile.thumb = newFile.blob;
          }
        } else {
          var reader = new FileReader();
          reader.onload = function (event) {
            newFile.blob = event.target.result;
            newFile.thumb = "";
            if (newFile.blob && newFile.type.substr(0, 6) === "image/") {
              newFile.thumb = newFile.blob;
            }
          }; // data url!
          reader.readAsDataURL(newFile);
        }

        // Thumbnails
        // 缩略图
      }
    },
    removeFile(file) {
      this.comment.attachments.splice(this.comment.attachments.indexOf(file), 1);
    },
    removeQueueFile(file) {
      const fileIndex = this.files.indexOf(file);
      if (fileIndex !== -1)
        this.files.splice(fileIndex, 1);
    },
    cancelUpload(file) {
      if (file.active)
        this.$refs.upload.active = false;
      else
        this.removeQueueFile(file);
    },
    insertTag() {
      const transaction = this.editor.state.tr.insertText(" @");
      this.editor.view.dispatch(transaction);
      this.editor.focus();
    },
  },
  mounted() {
    this.id = this._uid;
  },
  beforeDestroy() {
    this.destroyPopup();
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

$uploaderColor: rgba(57, 81, 155, 0.2) !default;

.fts-comment {
  .comment-file-attachments {
    margin-left: 1px;
    margin-bottom: .375rem;
  }
  .comment-wrapper {
    display: flex;
    align-items: flex-start;
    .comment-user-profile {
      margin-right: 0.75rem;
    }
    .comment-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      border: 1px solid var(--fts-gray-3);
      background: var(--fts-background);
      .comment-uploader {
        padding: .375rem .375rem 0;
      }
      .comment-editor {
        flex: 1;
        padding: 0.75rem;
        line-height: 1.5;
        font-size: 0.875rem;
        overflow: auto;
        & *.is-empty:nth-child(1)::before {
          content: attr(data-empty-text);
          float: left;
          color: var(--fts-form-control-placeholder);
          pointer-events: none;
          height: 0;
        }
        .editor__content {
          word-break: break-word;
        }
      }
      .comment-action-bar {
        display: flex;
        padding: 0.375rem 0.75rem;
        background: var(--fts-gray-1);
        font-size: 1rem;
        .comment-actions {
          flex: 1;
          display: flex;
          align-items: center;
          > div {
            margin-right: 0.75rem;
            &:not(:first-child) {
              margin-left: 0.75rem;
            }
          }
          .file-upload-container {
            display: flex;
            &.icon-handle {
              label {
                cursor: pointer;
              }
            }
            &:hover {
              .comment-action-icon {
                color: $primaryColor;
              }
            }
          }
          .comment-action-icon {
            color: var(--fts-gray-5);
            &:not(.disabled):hover {
              color: $primaryColor;
              cursor: pointer;
            }
            &.disabled {
              cursor: not-allowed;
            }
          }
        }
      }
    }
  }
}

.mention {
  @include text-overflow-ellipsis;
  max-width: 100%;
  display: inline-flex;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  background: var(--fts-gray-4);
  font-size: 0.75rem;
  font-weight: 500;
}
.mention,
.mention-suggestion {
  color: var(--fts-text);
}

.suggestion-list {
  padding: 0.2rem;
  font-size: 0.8rem;
  font-weight: 500;
  .suggestion-list__item {
    display: flex;
    align-items: center;
    margin-bottom: 0.2rem;
    padding: 0.2rem 0.5rem;
    border-radius: .25rem;
    cursor: pointer;
    &:last-child {
      margin-bottom: 0;
    }
    &.is-empty {
      opacity: 0.5;
    }
    &:hover,
    &.is-selected {
      background-color: darken(#333, 10%);
    }
    .user-profile-name {
      font-size: 0.75rem;
      font-weight: 500;
    }
    .user-profile-username {
      font-size: 0.675rem;
      font-weight: 300;
    }
  }
}
.suggestion-list__no-results {
  padding: 0.2rem 0.5rem;
}

.tippy-tooltip.dark-theme {
  padding: 0; 
  border-radius: .25rem;
  background-color: #333;
  color: #fff;
  font-size: 1rem;
  text-align: inherit;
  .tippy-backdrop {
    display: none;
  }
  .tippy-roundarrow {
    fill: #333;
  }
}
.tippy-popper[x-placement^="top"] .tippy-tooltip.dark-theme .tippy-arrow {
  border-top-color: #333;
}
.tippy-popper[x-placement^="bottom"] .tippy-tooltip.dark-theme .tippy-arrow {
  border-bottom-color: #333;
}
.tippy-popper[x-placement^="left"] .tippy-tooltip.dark-theme .tippy-arrow {
  border-left-color: #333;
}
.tippy-popper[x-placement^="right"] .tippy-tooltip.dark-theme .tippy-arrow {
  border-right-color: #333;
}
</style>